import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  applyActionCode,
  confirmPasswordReset,
  getAuth,
  verifyPasswordResetCode,
} from "firebase/auth";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Link } from "@tanstack/react-router";
import LoadingOverlay from "@/components/LoadingOverlay";
import { Check, X } from "lucide-react";

export const Route = createFileRoute("/_auth/auth-action")({
  component: AuthActionPage,
  staticData: {
    title: "Account Action",
  },
});

type ActionMode = "resetPassword" | "verifyEmail" | "recoverEmail";
type ActionState = "loading" | "success" | "error" | "input";

function AuthActionPage() {
  const [actionState, setActionState] = useState<ActionState>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [mode, setMode] = useState<ActionMode | null>(null);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const processAction = async () => {
      try {
        const auth = getAuth();
        const urlParams = new URLSearchParams(window.location.search);
        const actionMode = urlParams.get("mode") as ActionMode;
        const actionCode = urlParams.get("oobCode");

        if (!actionMode || !actionCode) {
          setActionState("error");
          setErrorMessage("Invalid action link. Please try again.");
          return;
        }

        setMode(actionMode);
        setOobCode(actionCode);

        switch (actionMode) {
          case "verifyEmail":
            await handleEmailVerification(auth, actionCode);
            break;
          case "resetPassword":
            await handlePasswordResetCheck(actionCode);
            break;
          case "recoverEmail":
            await handleEmailRecovery(auth, actionCode);
            break;
          default:
            setActionState("error");
            setErrorMessage("Unknown action type.");
        }
      } catch (error) {
        console.error("Error processing action:", error);
        setActionState("error");
        setErrorMessage("An unexpected error occurred. Please try again later.");
      }
    };

    processAction();
  }, []);

  const handleEmailVerification = async (auth: any, actionCode: string) => {
    try {
      await applyActionCode(auth, actionCode);
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
        await auth.currentUser.reload();
      }
      setActionState("success");
    } catch (error: any) {
      if (error.code === "auth/invalid-action-code") {
        setActionState("success");
      } else {
        setActionState("error");
        setErrorMessage("Failed to verify email. The link may have expired.");
      }
    }
  };

  const handlePasswordResetCheck = async (actionCode: string) => {
    try {
      await verifyPasswordResetCode(getAuth(), actionCode);
      setActionState("input");
    } catch (error: any) {
      setActionState("error");
      setErrorMessage("Invalid or expired password reset link.");
    }
  };

  const handlePasswordResetSubmit = async () => {
    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage("Password must be at least 6 characters long.");
      return;
    }

    try {
      await confirmPasswordReset(getAuth(), oobCode!, newPassword);
      setActionState("success");
    } catch (error: any) {
      setErrorMessage("Failed to reset password. Please try again.");
    }
  };

  const handleEmailRecovery = async (auth: any, actionCode: string) => {
    try {
      await applyActionCode(auth, actionCode);
      setActionState("success");
    } catch (error: any) {
      setActionState("error");
      setErrorMessage("Failed to recover email. The link may have expired.");
    }
  };

  if (actionState === "loading") {
    return <LoadingOverlay isLoading={true} />;
  }

  return (
    <div className="flex flex-col items-center justify-center gap-6 p-6 text-center">
      {actionState === "success" && (
        <>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10 text-success dark:bg-success/20 dark:text-success">
            <Check className="h-8 w-8" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-semibold">
            {mode === "verifyEmail" && "Email Verified Successfully! 🎉"}
            {mode === "resetPassword" && "Password Reset Successfully! 🎉"}
            {mode === "recoverEmail" && "Email Recovered Successfully! 🎉"}
          </h1>
          <p className="text-muted-foreground">
            {mode === "verifyEmail" &&
              "Awesome! Your account is now fully activated and ready to go. You're all set to start your trading journey!"}
            {mode === "resetPassword" &&
              "Your password has been successfully reset. You can now sign in with your new password."}
            {mode === "recoverEmail" &&
              "Your email has been successfully recovered."}
          </p>
          {mode === "verifyEmail" ? (
            <p className="text-muted-foreground">You may now close this page.</p>
          ) : (
            <div className="mt-4">
              <Link to="/signin">
                <Button>Go to Sign In</Button>
              </Link>
            </div>
          )}
        </>
      )}

      {actionState === "input" && mode === "resetPassword" && (
        <>
          <h1 className="text-2xl font-semibold">Reset Your Password</h1>
          <p className="text-muted-foreground">
            Enter your new password below.
          </p>
          <div className="w-full max-w-sm space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
            {errorMessage && (
              <p className="text-sm text-destructive">{errorMessage}</p>
            )}
            <Button
              onClick={handlePasswordResetSubmit}
              className="w-full"
              disabled={!newPassword || !confirmPassword}
            >
              Reset Password
            </Button>
          </div>
        </>
      )}

      {actionState === "error" && (
        <>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive dark:bg-destructive dark:text-destructive">
            <X className="h-8 w-8" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-semibold">
            Oops! Something Went Wrong
          </h1>
          <p className="text-muted-foreground">
            {errorMessage} Don't worry though, we can try again!
          </p>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row">
            <Link to="/signin">
              <Button variant="outline">Back to Sign In</Button>
            </Link>
            {mode === "resetPassword" && (
              <Link to="/forgot">
                <Button>Request New Reset Link</Button>
              </Link>
            )}
          </div>
        </>
      )}
    </div>
  );
}